<script lang="ts" setup>
import BaseCard from "@/components/BaseCard.vue";
import BaseCheckbox from "@/components/BaseCheckbox.vue";
import BaseContent from "@/components/BaseContent.vue";
import BaseCountdown from "@/components/BaseCountdown.vue";
import BaseHighlight from "@/components/BaseHighlight.vue";
import BaseSubContent from "@/components/BaseSubContent.vue";
import DokkanCharacterList from "@/components/DokkanCharacterList.vue";
import GenericItem from "@/components/GenericItem.vue";
import TheLayout from "@/components/TheLayout.vue";
import useToast from "@/composables/toast";
import { useUser } from "@/composables/user";
import { MissionCategory } from "@/types/mission";
import { router, usePage } from "@inertiajs/vue3";
import axios from "axios";
import { computed } from "vue";
import { route } from "ziggy-js";

const page = usePage();
const user = useUser(page.props.user);

const props = defineProps<{
  category: MissionCategory;
}>();

const title = computed(() => {
  return (
    props.category.campaign
      ? props.category.campaign.name +
        " Board " +
        String(props.category.id).slice(-1)
      : props.category.missions[0].name
  )
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
});

const description = computed(() => {
  return `'${props.category.missions[0].description}' and other Dokkan Battle missions!`
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
});

const ids = computed<number[]>(() =>
  props.category.missions.map((mission) => mission.id),
);
const allCompleted = computed<boolean>(() =>
  props.category.missions.every((mission) => mission.completed),
);

const markCompleted = (id: number | number[], completed: boolean) => {
  const multiple = id instanceof Array;

  axios
    .post(
      route(completed ? "users.missions.store" : "users.missions.destroy"),
      {
        missions: multiple ? id : [id],
      },
    )
    .then(() => {
      useToast({
        title: "Success",
        text: completed
          ? `Mission${multiple ? "s" : ""} marked as completed.`
          : `Mission${multiple ? "s" : ""} marked as not completed.`,
      });
    })
    .catch(() => {
      useToast({
        title: "Oops!",
        text: "Something went wrong. Please try again.",
        icon: "error",
      });
    })
    .finally(() => {
      router.reload({
        only: ["category"],
      });
    });
};
</script>

<template>
  <TheLayout
    :description="description"
    :head-image="category.campaign?.img ?? category.img"
    :heading-href="route('missions.index')"
    :title="title"
    heading="Missions"
  >
    <BaseCard
      :container="{ class: 'w-full max-w-xl mx-auto' }"
      class="!gap-0 !p-0"
    >
      <template v-if="category.campaign?.name" #heading>
        {{ category.campaign.name }}
        Board {{ String(category.id).slice(-1) }}
      </template>

      <div class="flex flex-col gap-4 p-4">
        <img
          v-if="category.campaign?.img ?? category.img"
          :alt="String(category.id)"
          :src="category.campaign?.img ?? category.img"
          class="w-full border border-black"
        />

        <div
          v-if="!category.indefinite && new Date(category.ends_at) > new Date()"
          class="self-center"
        >
          Ends in <BaseCountdown :end="category.ends_at" />.
        </div>
        <div
          v-else-if="new Date(category.ends_at) <= new Date()"
          class="self-center"
        >
          Mission period has ended
        </div>

        <div class="ml-auto">
          <BaseCheckbox
            v-if="user.isAuthenticated"
            :checked="allCompleted"
            @change="markCompleted(ids, !allCompleted)"
          >
            Completed
          </BaseCheckbox>

          <BaseHighlight v-else class="text-xs"
            >Sign in to track your progress!</BaseHighlight
          >
        </div>
      </div>

      <BaseContent
        v-for="mission in category.missions"
        :key="mission.id"
        :class="{ '!to-yellow-300/15': mission.completed }"
        :header="mission.name"
        class="border-t border-green-900 bg-gradient-to-bl from-transparent to-transparent p-4"
        collapsed
        collapsible
      >
        <p v-html="mission.description" />

        <BaseSubContent header="Rewards">
          <div
            :style="`grid-template-columns: repeat(auto-fit, 75px);`"
            class="mt-2 grid w-full place-content-around gap-4"
          >
            <div
              v-for="reward in mission.rewards"
              :key="`${reward.item?.type}.${reward.item.id}`"
              class="flex flex-col items-center gap-1"
            >
              <GenericItem
                :item="reward.item"
                :scale="0.75"
                :type="reward.item_type"
              />

              <span>
                x<BaseHighlight>{{
                  reward.quantity * (reward.item.amount ?? 1)
                }}</BaseHighlight></span
              >
            </div>
          </div>
        </BaseSubContent>

        <BaseSubContent
          v-if="mission.characters.length"
          header="Specified Characters"
        >
          <DokkanCharacterList :characters="mission.characters" :scale="0.67" />
        </BaseSubContent>

        <div class="ml-auto">
          <BaseCheckbox
            v-if="user.isAuthenticated"
            :checked="mission.completed"
            @change="markCompleted(mission.id, !mission.completed)"
          >
            Completed
          </BaseCheckbox>

          <BaseHighlight v-else class="text-xs"
            >Sign in to track your progress!</BaseHighlight
          >
        </div>
      </BaseContent>
    </BaseCard>
  </TheLayout>
</template>

<style scoped></style>
